import type { ResolvedColorScheme } from "./color-scheme";
import { isWithinIframe } from "./iframe";

/** The query parameter the framing page sets on the frame's address. */
export const FRAME_COLOR_SCHEME_PARAM = "color-scheme";

/** Where the scheme the frame was opened with is kept for the frame's later pages. */
export const FRAME_COLOR_SCHEME_STORAGE_KEY = "metabase.frame-color-scheme";

const isResolvedColorScheme = (
  value: string | null,
): value is ResolvedColorScheme => value === "light" || value === "dark";

/**
 * The colour scheme the page that frames Metabase asks for, so that a framed Metabase looks
 * like its host: `?color-scheme=light` or `?color-scheme=dark` on the frame's address. The
 * value is kept in the tab's session storage, so a later address of the frame (a sign-in
 * redirect, a reload, a page reached from a link) keeps the host's scheme without carrying the
 * parameter. Null when the page is not in a frame, or when the host named no scheme: the
 * person's own setting applies then.
 */
export function getFrameColorScheme(): ResolvedColorScheme | null {
  if (!isWithinIframe()) {
    return null;
  }
  const asked = new URLSearchParams(window.location.search).get(
    FRAME_COLOR_SCHEME_PARAM,
  );
  let scheme: ResolvedColorScheme | null = isResolvedColorScheme(asked)
    ? asked
    : null;
  try {
    if (scheme) {
      window.sessionStorage.setItem(FRAME_COLOR_SCHEME_STORAGE_KEY, scheme);
    } else {
      const kept = window.sessionStorage.getItem(
        FRAME_COLOR_SCHEME_STORAGE_KEY,
      );
      scheme = isResolvedColorScheme(kept) ? kept : null;
    }
  } catch {
    // Session storage can be unavailable (a blocked third-party frame); the parameter alone then counts.
  }
  return scheme;
}
